import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { TextInput } from '@mantine/core';
import { SectionCard } from '../../shared/SectionCard/SectionCard';
import { asCurrency, asHour, getCanceledOrderlines } from '../../shared/utils/dashboard-page.utils';
import { SectionKey } from '../../shared/types/dashboard-page.types';
import {
  AdminApprovalRequestDetailView,
  adminApprovalRequestsService,
} from '../../../../services/admin-approval-requests/admin-approval-requests.service';
import classes from './cancellations-section.module.scss';

export type CancellationsSectionProps = {
  collapsedSections: Record<SectionKey, boolean>;
  toggleSection: (section: SectionKey) => void;
  tenantId: string;
  sessionIds: number[];
  canonicalItemsCount?: number;
  canonicalItemsAmount?: number;
};

const PAGE_SIZE = 10;

const REASON_LABELS: Record<string, string> = {
  cancel_order: 'ביטול הזמנה',
  vip: 'VIP',
  waiter_error: 'טעות במנה',
  staff: 'שת"פ',
  other: 'אחר',
  manager_action: 'פעולת מנהל',
};

const getCancelCost = (detail: AdminApprovalRequestDetailView): number => getCanceledOrderlines(detail).reduce((sum, line) => sum + line.unitPrice * line.quantity, 0);

// order_code is "TenantName/sessionSeq/orderSeq" — the tenant name is redundant on a single-tenant dashboard, so show only "sessionSeq/orderSeq".
const getOrderNumber = (orderCode: string | null): string => (orderCode ? orderCode.split('/').slice(-2).join('/') : '—');

const getCancelItemsLabel = (detail: AdminApprovalRequestDetailView): string => getCanceledOrderlines(detail)
  .map((line) => (line.quantity > 1 ? `${line.productName ?? 'פריט'} ${line.quantity}x` : (line.productName ?? 'פריט')))
  .join(', ');

const matchesSearch = (detail: AdminApprovalRequestDetailView, query: string): boolean => {
  if (!query) {
    return true;
  }
  const haystack = [
    detail.requestedByName,
    REASON_LABELS[detail.reason] ?? detail.reason,
    detail.note ?? '',
    detail.orderCode ?? '',
    detail.tableLabel ?? '',
    getCancelItemsLabel(detail),
  ].join(' ').toLowerCase();
  return haystack.includes(query.toLowerCase());
};

export const CancellationsSection: React.FC<CancellationsSectionProps> = ({
  collapsedSections,
  toggleSection,
  tenantId,
  sessionIds,
  canonicalItemsCount,
  canonicalItemsAmount,
}) => {
  const [search, setSearch] = useState('');
  const [cancelDetails, setCancelDetails] = useState<AdminApprovalRequestDetailView[]>([]);
  const [page, setPage] = useState(1);
  const [totalRows, setTotalRows] = useState(0);
  const [totalCost, setTotalCost] = useState(0);
  const loadingRef = useRef(false);
  const generationRef = useRef(0);
  const sessionKey = sessionIds.join(',');

  const loadPage = useCallback(async (nextPage: number, replace: boolean) => {
    if (!tenantId || !sessionKey || loadingRef.current) return;
    loadingRef.current = true;
    const generation = generationRef.current;
    try {
      const result = await adminApprovalRequestsService.list({
        page: nextPage,
        pageSize: PAGE_SIZE,
        session: sessionKey.split(','),
        actionType: ['cancel'],
        status: ['approved'],
      });
      const details = await Promise.allSettled(result.rows.map((row) => adminApprovalRequestsService.getDetail(row.id)));
      if (generationRef.current !== generation) return;
      const loaded = details
        .filter((entry): entry is PromiseFulfilledResult<AdminApprovalRequestDetailView> => entry.status === 'fulfilled')
        .map((entry) => entry.value);
      setCancelDetails((previous) => replace
        ? loaded
        : [...previous, ...loaded.filter((detail) => !previous.some((existing) => existing.id === detail.id))]);
      setPage(nextPage);
      setTotalRows(result.total);
      setTotalCost(result.totalCost ?? 0);
    } finally {
      if (generationRef.current === generation) loadingRef.current = false;
    }
  }, [sessionKey, tenantId]);

  useEffect(() => {
    generationRef.current += 1;
    loadingRef.current = false;
    setCancelDetails([]);
    setPage(1);
    setTotalRows(0);
    setTotalCost(0);
    void loadPage(1, true);
  }, [loadPage]);

  const handleListScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const target = event.currentTarget;
    if (target.scrollHeight - target.scrollTop - target.clientHeight < 80 && page * PAGE_SIZE < totalRows) {
      void loadPage(page + 1, false);
    }
  };

  const visibleDetails = useMemo(
    () => cancelDetails.filter((detail) => matchesSearch(detail, search)),
    [cancelDetails, search],
  );

  return (
    <SectionCard
      title="ביטולים"
      sectionKey="cancellations"
      collapsed={collapsedSections.cancellations}
      onToggle={() => toggleSection('cancellations')}
      titleExtra={(
        <>
          <span className={classes.countBadge}>{`(${canonicalItemsCount ?? totalRows})`}</span>
          <span className={classes.costBadge}>{`עלות: ${asCurrency(canonicalItemsAmount ?? totalCost)}`}</span>
        </>
      )}
    >
      <TextInput
        className={classes.searchInput}
        placeholder="חיפוש לפי הזמנה, מבטל, שולחן, פריטים, עלות..."
        value={search}
        onChange={(event) => setSearch(event.currentTarget.value)}
        data-testid="dashboard-cancellations-search"
      />

      {visibleDetails.length === 0 ? (
        <p className={classes.emptyHint}>אין ביטולים להצגה</p>
      ) : (
        <div className={classes.list} onScroll={handleListScroll} data-testid="dashboard-cancellations-list">
          {visibleDetails.map((detail) => (
            <div key={detail.id} className={classes.card}>
              <div className={classes.cell}>
                <span className={classes.cellLabel}>שעה</span>
                <span className={classes.cellValue}>{asHour(detail.createdAt)}</span>
              </div>
              <div className={classes.cell}>
                <span className={classes.cellLabel}>מבטל</span>
                <span className={classes.cellValue}>{detail.requestedByName}</span>
              </div>
              <div className={classes.cell}>
                <span className={classes.cellLabel}>סיבה</span>
                <span className={classes.cellValue}>{REASON_LABELS[detail.reason] ?? detail.reason}</span>
              </div>
              <div className={classes.cell}>
                <span className={classes.cellLabel}>הערה</span>
                <span className={classes.cellValue}>{detail.note ?? '—'}</span>
              </div>
              <div className={classes.cell}>
                <span className={classes.cellLabel}>הזמנה</span>
                <span className={classes.cellValue}>{getOrderNumber(detail.orderCode)}</span>
              </div>
              <div className={classes.cell}>
                <span className={classes.cellLabel}>שולחן</span>
                <span className={classes.cellValue}>{detail.tableLabel ?? '—'}</span>
              </div>
              <div className={classes.cell}>
                <span className={classes.cellLabel}>פריטים</span>
                <span className={classes.cellValue}>{getCancelItemsLabel(detail) || '—'}</span>
              </div>
              <div className={classes.cell}>
                <span className={classes.cellLabel}>עלות</span>
                <span className={`${classes.cellValue} ${classes.cellValueDanger}`}>{asCurrency(getCancelCost(detail))}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </SectionCard>
  );
};
